import { Box, MenuItem, PaginationItem, styled, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { TooltipCC } from 'components/common';
import { IconFirstPaging, IconLastPaging, IconNextPaging, IconPreviousPaging } from 'components/common/IconCommon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { toastError } from 'utils/helpers';

const ELLIPSIS_CLASSNAME = {
    START: 'start-ellipsis',
    END: 'end-ellipsis',
    SPECIAL: 'no-style-'
}

const DEFAULT_CHANGE_ELLIPSIS_PAGE = 5
const LIMIT_OPTIONS = [5, 10, 20, 50, 100]

const PaginationTableWrapper = styled(Stack)(({ theme }) => ({
    justifyContent: 'space-between',
    alignItems: 'end',
    borderRadius: 6,
    marginTop: 12,
    padding: '8px 16px',
    [theme.breakpoints.up("sm")]: {
        flexDirection: 'row',
        alignItems: 'center'
    }
}))

export default function PaginationButtons({ handleChangePage, handleChangeLimit, useDynamicLimit = false, page, limit, total, sxStyle = {} }) {
    const { t } = useTranslation()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const [toPage, setToPage] = useState(page || 1)
    const [value] = useDebounce(toPage, 1000);
    const [enterPage, setEnterPage] = useState(false)

    const handleToPage = (e) => {
        setEnterPage(true)
        const valueToPage = e.target.value
        valueToPage < 0 ? setToPage(valueToPage * -1) : setToPage(valueToPage)
    }

    useEffect(() => {
        if (enterPage) {
            if (Math.ceil(total / limit) >= value && value > 0) {
                handleChangePage('', value)
                setEnterPage(false)
            } else {
                toastError(`${t('paging.onlyPageToPage', { page: Math.ceil(total / limit) })}`)
                setEnterPage(false)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    useEffect(() => {
        setToPage(page + 1)
    }, [page])


    const handleClickPage = (e) => {
        const classStartEllipsis = ELLIPSIS_CLASSNAME.SPECIAL + ELLIPSIS_CLASSNAME.START
        const classEndEllipsis = ELLIPSIS_CLASSNAME.SPECIAL + ELLIPSIS_CLASSNAME.END
        const className = e?.target?.className

        if (className?.includes(classEndEllipsis)) {
            handleChangePage('', Math.min(page + (DEFAULT_CHANGE_ELLIPSIS_PAGE + 1), Math.ceil(total / limit)))
        } else if (className?.includes(classStartEllipsis)) {
            handleChangePage('', Math.max(page - (DEFAULT_CHANGE_ELLIPSIS_PAGE - 1), 1))
        } else { }
    }

    const handleLimitChange = (e) => {
        handleChangeLimit?.(Number(e.target.value))
    }

    const renderButtonTooltip = (label) => {
        if (label === "Go to first page") {
            return "paging.first"
        }
        if (label === "Go to previous page") {
            return "paging.prev"
        }
        if (label === "Go to next page") {
            return "paging.next"
        }
        if (label === "Go to last page") {
            return "paging.last"
        }
        return ""
    }

    return (
        <PaginationTableWrapper spacing={2} sx={{ ...sxStyle }}>
            <Box
                alignItems={'center'}
                gap={1}
                sx={{
                    display: 'inline-flex',
                    width: 'fit-content',
                    flexShrink: 0,
                    whiteSpace: 'nowrap'
                }}
            >
                <Typography>
                    {t('table.visibleNum', { num: page * limit + 1, endNum: Math.min(page * limit + limit, total), total: total })}
                </Typography>
                {useDynamicLimit && handleChangeLimit && (
                    <TextField
                        select
                        value={limit}
                        onChange={handleLimitChange}
                        sx={{
                            width: `${String(limit).length + 4}ch`,
                            minWidth: '48px',
                            '& .MuiSelect-select': {
                                py: 1,
                                pl: 1,
                                pr: '24px !important'
                            }
                        }}
                    >
                        {LIMIT_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Box>
            <Pagination
                onClick={handleClickPage}
                sx={{ marginTop: '0 !important' }} variant="outlined" shape="rounded" onChange={handleChangePage} page={page + 1} count={Math.ceil(total / limit)} showFirstButton showLastButton siblingCount={isMobile ? 0 : 1} boundaryCount={isMobile ? 1 : 1} renderItem={(item) => {
                    if (item.type === "end-ellipsis" || item.type === "start-ellipsis") {
                        return (
                            <TooltipCC title={t('paging.' + ELLIPSIS_CLASSNAME.SPECIAL + item.type, { num: DEFAULT_CHANGE_ELLIPSIS_PAGE })}>
                                <span style={{ cursor: 'pointer' }}>
                                    <PaginationItem
                                        slots={{ previous: IconPreviousPaging, next: IconNextPaging, first: IconFirstPaging, last: IconLastPaging }}
                                        className={ELLIPSIS_CLASSNAME.SPECIAL + item.type}
                                        {...item}
                                    />
                                </span>
                            </TooltipCC>
                        )
                    } else {
                        const tooltipKey = renderButtonTooltip(item["aria-label"]);
                        return (
                            <TooltipCC title={tooltipKey ? t(tooltipKey) : ""}>
                                <span>
                                    <PaginationItem slots={{ previous: IconPreviousPaging, next: IconNextPaging, first: IconFirstPaging, last: IconLastPaging }} {...item} />
                                </span>
                            </TooltipCC>
                        )
                    }
                }} />
            <Box
                alignItems={'center'}
                gap={2}
                sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    width: 'fit-content',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    marginTop: '0 !important',
                    color: theme => theme.palette.text.primary
                }}
            >
                {t('paging.goToPage')}
                <TextField
                    value={toPage}
                    onChange={handleToPage}
                    type='number'
                    sx={{
                        width: `${Math.max(String(toPage || '').length, 1) + 3}ch`,
                        minWidth: '40px',
                        maxWidth: '72px',
                        '& .MuiInputBase-input': {
                            px: 0.5,
                            textAlign: 'center'
                        }
                    }}
                />
            </Box>
        </PaginationTableWrapper>
    );
}
